import React, { useState, useEffect, useRef, useMemo } from "react";
import { Plus, Star, CheckCircle2, AlertTriangle, Share2, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import ReviewCard from "@/components/ReviewCard";
import ReviewForm from "@/components/ReviewForm";
import ReviewThankYou from "@/components/ReviewThankYou";
import RatingBreakdown from "./RatingBreakdown";
import {
  Review,
  PerSourceStat,
  CreateReviewRequest,
  ReviewsResponse,
  type ReviewActivityRow,
} from "@/types/review";
import {
  reviewService,
  type AssignReviewUserResult,
  type ReviewAuthorHiddenLabel,
} from "@/api/reviewService";
import { markReviewAsCommented, type StoreSearchResult as StoreSearchHit } from "@/api/fbGroupReviewService";
import { requestWithAuth, ApiError } from "@/api/request";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { useReviewThankYou } from "@/hooks/useReviewThankYou";
import { UNCATEGORIZED, type StoreEventProps } from "@/lib/posthog";
import { pickCommentTemplateFor, renderCommentText } from "@/lib/commentTemplates";
import { useSearchParams } from "@/hooks/useSearchParams";
import {
  applyReviewFilterParams,
  buildReviewShareUrl,
  parseReviewFilterParams,
} from "@/lib/reviewFilterParams";
import { toast } from "sonner";
import { hasFocusParams, parseFocusParams, stripFocusParams, type FocusParams } from "@/lib/focusParams";
import { usePageContext } from "vike-react/usePageContext";
import { NOTIFICATION_CHROME } from "@shared/notificationCopy";

// Arabic copy for the admin activity trail. Keyed off the backend action enum; an action this
// table does not know renders as its raw key rather than disappearing.
const ACTIVITY_ACTION_AR: Record<ReviewActivityRow["action"], string> = {
  created: "كتب التقييم",
  imported: "تم استيراد التقييم",
  edited: "عدّل التقييم",
  images_added: "أضاف صور",
  deleted: "حذف التقييم نهائياً",
  soft_deleted: "أخفى التقييم",
  restored: "استرجع التقييم",
  store_reassigned: "نقل التقييم لمتجر آخر",
  user_linked: "ربط التقييم بمستخدم",
  user_unlinked: "فك ربط التقييم بالمستخدم",
  flagged: "استبعده كاشف التقييمات المزيفة",
  unflagged: "أُلغي استبعاده",
  purchase_refunded: "تم استرداد قيمة الطلب",
  provenance_updated: "عدّل بيانات المصدر",
};

const ACTIVITY_FIELD_AR: Record<string, string> = {
  text: "النص",
  overallRating: "التقييم",
  ratingFactors: "التفاصيل",
  storeId: "المتجر",
  userId: "المستخدم",
  sourceRemoved: "المنشور الأصلي محذوف",
  authorHidden: "اسم صاحب التقييم",
};

const ACTIVITY_SYSTEM_LABEL_AR: Record<string, string> = {
  fb_group_agent: "وكيل جروبات فيسبوك",
  fb_group_agent_review_from_post: "وكيل جروبات فيسبوك",
  fb_group_comment: "وكيل جروبات فيسبوك (تعليق)",
  google_import: "استيراد جوجل",
  facebook_page_import: "استيراد صفحة فيسبوك",
  shopify_widget: "ويدجت المتجر",
  whatsapp_flow: "استبيان واتساب",
  reconciliation: "المطابقة الآلية",
  authenticity_sweep: "كاشف التقييمات المزيفة",
  bulk_soft_delete: "إخفاء جماعي",
  bulk_restore: "استرجاع جماعي",
};

const describeActivityActor = (actor: ReviewActivityRow["actor"]): string => {
  if (actor.kind === "system") {
    return ACTIVITY_SYSTEM_LABEL_AR[actor.label ?? ""] ?? actor.label ?? "النظام";
  }
  const name = actor.name ?? "مستخدم";
  if (actor.kind === "admin") return `${name} (أدمن)`;
  if (actor.kind === "moderator") return `${name} (مشرف)`;
  return name;
};

const formatActivityValue = (value: unknown): string => {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return "—";
    return entries.map(([k, v]) => `${k}: ${String(v)}`).join("، ");
  }
  return String(value);
};

// One row of `GET /admin/users?search=` — the picker in the link-to-user dialog.
type AdminUserHit = {
  _id: string;
  full_name?: string;
  email?: string;
  phoneNumber?: string;
  avatar_url?: string;
  points?: number;
};

const resetAssignUserDialog = ({
  setAssigningReview,
  setSelectedUser,
  setUserSearchQuery,
  setUserSearchResults,
}: {
  setAssigningReview: (r: Review | null) => void;
  setSelectedUser: (u: AdminUserHit | null) => void;
  setUserSearchQuery: (q: string) => void;
  setUserSearchResults: (r: AdminUserHit[]) => void;
}) => {
  setAssigningReview(null);
  setSelectedUser(null);
  setUserSearchQuery("");
  setUserSearchResults([]);
};

// ─── FB comment templates: pool comes from lib/commentTemplates ─────────────

interface ReviewsSectionProps {
  storeId: string;
  storeName: string;
  storeLogoUrl?: string;
  categorySlug?: string;
  initialReviewsData?: unknown;
  storeStatus?: string;
  // O2 (SEO): enables the SSR'd anchor to the crawlable /store/<slug>/reviews
  // archive. Must stay a plain <a> in server HTML — it's how crawlers discover
  // the paginated review corpus.
  storeSlug?: string;
  // Standard store props for analytics. Separate from `categorySlug` above, which
  // selects the rating-factor set and resolves differently (legacy category only).
  trackingProps?: StoreEventProps;
}

// request() unwraps { ok, data } → each fetched page is ReviewsResponse["data"] at runtime.
type ReviewsPage = ReviewsResponse["data"];

const SOURCE_LABELS: Record<string, string> = {
  all: "الكل",
  yetfarrag: "يتفرج",
  "google_maps": "Google Maps",
  facebook: "Facebook",
  // Legacy umbrella key: still honoured by the API for old shared links, never emitted as a pill.
  facebook_group: "مجموعات FB",
  facebook_group_post: "منشورات FB",
  facebook_group_comment: "تعليقات FB",
  whatsapp_flow: "واتساب",
  shopify_widget: "متجر شوبيفاي",
  // Scraped from the merchant's on-site Shopify review app — see ReviewCard.getSourceInfo.
  loox: "متجر شوبيفاي",
};

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  storeId,
  storeName,
  storeLogoUrl,
  categorySlug,
  initialReviewsData,
  storeStatus,
  storeSlug,
  trackingProps,
}) => {
  const { user, showAuthModal } = useAuth();
  const queryClient = useQueryClient();
  const { trackStore, trackReview } = usePostHogTracking();
  const storeTrackingProps: StoreEventProps = trackingProps ?? {
    store_id: storeId,
    store_slug: storeSlug,
    category_slug: categorySlug || UNCATEGORIZED,
    store_status: storeStatus,
  };
  const [showForm, setShowForm] = useState(false);
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  // The inline thank-you state that replaces the old success toast: the submitted score (whose
  // tone the copy follows), the points the ledger actually paid, and which elements the review is
  // still missing. `rating === null` means "nothing submitted this session".
  const thankYou = useReviewThankYou();
  const thankYouRating = thankYou.rating;

  // ─── FB comment flow ────────────────────────────────────────────────────────
  const [fbPendingReview, setFbPendingReview] = useState<Review | null>(null);
  const [fbConfirmOpen, setFbConfirmOpen] = useState(false);
  const [fbMarking, setFbMarking] = useState(false);
  const fbVisListenerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (fbVisListenerRef.current)
        document.removeEventListener("visibilitychange", fbVisListenerRef.current);
    };
  }, []);

  const handleFbCommentFlow = (review: Review) => {
    const postUrl = review.sourceData?.postUrl as string | undefined;
    if (!postUrl) return;
    const storeSlug = window.location.pathname.split("/store/")[1]?.split("/")[0] ?? "";
    // allowExternalLinksInComments: true reproduces the unspaced yetfarrag.com/store/<slug> this button has always pasted; this change unifies the template pool only, not link rendering.
    navigator.clipboard
      .writeText(renderCommentText({ template: pickCommentTemplateFor(review), storeSlug, allowExternalLinksInComments: true }))
      .catch(() => {});
    setFbPendingReview(review);
    window.open(postUrl, "_blank");
    if (fbVisListenerRef.current)
      document.removeEventListener("visibilitychange", fbVisListenerRef.current);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        document.removeEventListener("visibilitychange", onVisible);
        fbVisListenerRef.current = null;
        setFbConfirmOpen(true);
      }
    };
    setTimeout(() => {
      document.addEventListener("visibilitychange", onVisible);
      fbVisListenerRef.current = onVisible;
    }, 1000);
  };

  const handleFbConfirm = async (confirmed: boolean) => {
    setFbConfirmOpen(false);
    if (!confirmed || !fbPendingReview) {
      setFbPendingReview(null);
      return;
    }
    setFbMarking(true);
    const result = await markReviewAsCommented(fbPendingReview._id);
    if (result.ok) {
      // Infinite-query cache shape: { pages: ReviewsPage[], pageParams }. The old code
      // targeted a phantom `.data` level and was a silent no-op (quick-260815-dhs).
      queryClient.setQueryData(
        ["reviews", storeId, sortBy, sourceFilter, ratingFilter, imagesOnly, authTab],
        (old: { pages?: ReviewsPage[]; pageParams?: unknown[] } | undefined) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((pg) => ({
              ...pg,
              reviews: (pg?.reviews ?? []).map((r: Review) =>
                r._id === fbPendingReview._id
                  ? { ...r, commented: { at: new Date().toISOString() } }
                  : r
              ),
            })),
          };
        }
      );
    }
    setFbPendingReview(null);
    setFbMarking(false);
  };

  const openReviewForm = () => {
    // The form and the thank-you state are never both visible.
    thankYou.close();
    if (storeStatus === "draft") {
      setShowDraftConfirm(true);
    } else {
      setShowForm(true);
    }
  };
  // Guests get prompted to sign in; logged-in users open the review form.
  const handleReviewCta = () => {
    trackReview.ctaClicked({
      surface: "reviews_section",
      authenticated: !!user,
      ...storeTrackingProps,
    });
    if (!user) {
      showAuthModal({ trigger: "write_review", storeSlug });
      return;
    }
    openReviewForm();
  };
  // Which authenticity slice is being read. The SERVER filters — a client-side split cannot work
  // on a server-paginated list, because a page that happens to be one whole campaign would leave
  // the trusted tab rendering nothing.
  const [authTab, setAuthTab] = useState<"trusted" | "flagged">("trusted");
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [reassigningReview, setReassigningReview] = useState<Review | null>(null);
  // Admin: link an imported review to a registered account. Mirrors the store-reassign dialog.
  const [assigningReview, setAssigningReview] = useState<Review | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<AdminUserHit[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUserHit | null>(null);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const userSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Admin: the review whose activity trail is open.
  const [activityReview, setActivityReview] = useState<Review | null>(null);
  const [storeSearchQuery, setStoreSearchQuery] = useState("");
  const [storeSearchResults, setStoreSearchResults] = useState<StoreSearchHit[]>([]);
  const [selectedStore, setSelectedStore] = useState<StoreSearchHit | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Shareable reviews view: seed the three filters from the URL once, on first render.
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialFilters = useMemo(() => parseReviewFilterParams(searchParams), []);

  const [sortBy, setSortBy] = useState<
    "newest" | "oldest" | "rating_high" | "rating_low" | "most_liked"
  >(initialFilters.state.sort);
  const [sourceFilter, setSourceFilter] = useState<string>(initialFilters.state.source);
  const [ratingFilter, setRatingFilter] = useState<number | undefined>(
    initialFilters.state.rating
  );
  const [imagesOnly, setImagesOnly] = useState<boolean>(initialFilters.state.images === "with");

  // Keep the URL in sync with the active filters (replaceState — no history entries).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const nextSearch = applyReviewFilterParams(window.location.search, {
      source: sourceFilter,
      rating: ratingFilter,
      sort: sortBy,
      images: imagesOnly ? "with" : undefined,
    });
    if (nextSearch === window.location.search) return;
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + nextSearch + window.location.hash
    );
  }, [sourceFilter, ratingFilter, sortBy, imagesOnly]);

  // Arriving with filter params means the visitor was sent a filtered view — scroll to it.
  useEffect(() => {
    if (!initialFilters.hasAny || typeof window === "undefined") return;
    const timer = setTimeout(() => {
      document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?writeReview=1 — the intent handed over by the SSR-only review archive at
  // /store/{slug}/reviews, which cannot open a form itself.
  //
  // Read from `window.location.search` rather than the `useSearchParams` value on purpose: this
  // effect must run exactly ONCE (empty deps, same shape as the scroll effect above), and the hook's
  // value memoizes on the search string, which this effect itself rewrites.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    // Strict `=== "1"`, never truthiness: the param is attacker-controllable via any inbound link.
    if (params.get("writeReview") !== "1") return;

    // STRIP FIRST, before anything else. The filter-sync effect above calls
    // applyReviewFilterParams(window.location.search, ...), which PRESERVES unrelated params — a
    // surviving writeReview would be written straight back into the URL on the next filter change
    // and re-trigger on a back-nav. Only pathname + the remaining search + hash are written back;
    // no query VALUE is ever interpolated into the pushed URL.
    params.delete("writeReview");
    const remaining = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (remaining ? `?${remaining}` : "") + window.location.hash
    );

    // The SAME function the visible button calls, so the guest auth modal, the draft-store
    // confirm and the review_cta_clicked capture all still apply. A crafted link must not be able
    // to open the form for a logged-out visitor or skip the draft warning.
    handleReviewCta();

    const timer = setTimeout(() => {
      document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?focusReview=<id>[&focusComment=<id>] — a notification deep link (Phase 46, D-09). Page 1
  // of the SSR'd list rarely holds the target, so the review is fetched on its own and pinned
  // above the list. Same once-only shape as the writeReview effect above, and for the same
  // reason the params are STRIPPED FIRST so the filter-sync effect never writes them back.
  // Runs only in the browser: SSR markup and the store page's meta tags are unchanged.
  const [focusTarget, setFocusTarget] = useState<FocusParams | null>(null);
  const [focusHighlighted, setFocusHighlighted] = useState(false);
  const focusLandedRef = useRef(false);
  // Keyed on Vike's urlOriginal, not run once on mount: client-side navigation (a bell dropdown or
  // inbox <Link> while already on a store page) re-renders this component without remounting it.
  // Our own replaceState below does not change urlOriginal, so stripping never re-triggers it, and
  // a URL without focus params leaves the current pin alone. Each new landing is one-shot again.
  const { urlOriginal } = usePageContext();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const { search, pathname, hash } = window.location;
    const parsed = parseFocusParams(search);
    if (hasFocusParams(search)) {
      window.history.replaceState(window.history.state, "", pathname + stripFocusParams(search) + hash);
    }
    // parseFocusParams already rejected anything that is not a 24-hex ObjectId.
    if (!parsed.reviewId) return;
    focusLandedRef.current = false;
    setFocusHighlighted(false);
    setFocusTarget(parsed);
  }, [urlOriginal]);

  // A query rather than a one-shot fetch so the pinned card stays live: every like/comment/
  // delete mutation invalidates the ["reviews", storeId] prefix, which includes this key.
  const focusedQuery = useQuery({
    queryKey: ["reviews", storeId, "focused", focusTarget?.reviewId],
    queryFn: () => reviewService.getReviewById(focusTarget!.reviewId!),
    enabled: Boolean(focusTarget?.reviewId),
    staleTime: 5 * 60 * 1000,
  });
  // A review from another store is treated as missing: a crafted link must not pin it here.
  const focusedReview: Review | null =
    focusedQuery.data?.ok &&
    String((focusedQuery.data.data as Review & { storeId?: unknown }).storeId ?? "") ===
      String(storeId)
      ? focusedQuery.data.data
      : null;

  useEffect(() => {
    if (!focusTarget?.reviewId || focusLandedRef.current || !focusedQuery.data) return;
    focusLandedRef.current = true;
    if (!focusedReview) {
      // Deleted, hidden, flagged, or not on this store. The page itself renders normally.
      toast(NOTIFICATION_CHROME.focusNotFoundToast);
      setFocusTarget(null);
      return;
    }
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    let clearTimer: ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(() => {
      const card = document.getElementById(`review-${focusedReview._id}`);
      const comment = focusTarget.commentId
        ? document.getElementById(`comment-${focusTarget.commentId}`)
        : null;
      (comment ?? card)?.scrollIntoView({ block: "start", behavior: reduced ? "auto" : "smooth" });
      card?.focus({ preventScroll: true });
      setFocusHighlighted(true);
      clearTimer = setTimeout(() => setFocusHighlighted(false), 2400);
    }, 100);
    return () => {
      clearTimeout(timer);
      if (clearTimer) clearTimeout(clearTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedQuery.data, focusTarget]);

  const handleShareReviews = async () => {
    if (typeof window === "undefined") return;
    const url = buildReviewShareUrl({
      origin: window.location.origin,
      pathname: window.location.pathname,
      search: window.location.search,
      state: {
        source: sourceFilter,
        rating: ratingFilter,
        sort: sortBy,
        images: imagesOnly ? "with" : undefined,
      },
    });
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: storeName,
          text: `تقييمات ${storeName} على يتفرج`,
          url,
        });
        trackStore.shared({
          method: "native",
          surface: "reviews_section",
          ...storeTrackingProps,
        });
      } catch {
        // User cancelled the share sheet — not a failure.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      trackStore.shared({
        method: "clipboard",
        surface: "reviews_section",
        ...storeTrackingProps,
      });
      toast.success("تم نسخ رابط التقييمات");
    } catch {
      toast.error("تعذر نسخ الرابط");
    }
  };

  // Fetch stats for filter counts — request() unwraps to ReviewStats directly
  const { data: stats } = useQuery({
    queryKey: ["reviewStats", storeId],
    queryFn: () => reviewService.getStoreReviewStats({ storeId }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch reviews with filters — infinite query so تحميل المزيد APPENDS pages instead of
  // replacing them (quick-260815-dhs). Filter/sort changes reset naturally via the queryKey.
  const {
    data: reviewsData,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // authTab is part of the key ON PURPOSE: it is what resets pagination when the reader
    // switches tabs, so no manual reset logic is needed. Existing
    // invalidateQueries({ queryKey: ["reviews", storeId] }) calls still match as a prefix.
    queryKey: ["reviews", storeId, sortBy, sourceFilter, ratingFilter, imagesOnly, authTab],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      reviewService.getStoreReviews({
        storeId,
        page: pageParam,
        limit: 5,
        sortBy,
        source: sourceFilter !== "all" ? sourceFilter : undefined,
        rating: ratingFilter,
        imageFilter: imagesOnly ? "with" : undefined,
        authenticity: authTab,
      }) as unknown as Promise<ReviewsPage>,
    getNextPageParam: (last) =>
      last?.pagination?.hasNextPage ? (last.pagination.currentPage ?? 1) + 1 : undefined,
    staleTime: 5 * 60 * 1000,
    // SSR seed — same condition as the old query (page 1 disappears with the page state).
    // The SSR seed is fetched with authenticity=trusted, so it may only hydrate that tab.
    ...(initialReviewsData &&
    sortBy === "newest" &&
    sourceFilter === "all" &&
    !ratingFilter &&
    !imagesOnly &&
    authTab === "trusted"
      ? {
          initialData: {
            pages: [initialReviewsData as ReviewsPage],
            pageParams: [1],
          },
        }
      : {}),
  });

  // The category's rating-factor keys, for the thank-you card's factors upsell. Fetched HERE
  // rather than inside `ReviewThankYou`, which performs no network calls of its own. Writing
  // factor keys the store's category does not use would produce a breakdown nothing renders,
  // so an empty result simply means the factors row is not offered.
  const activityQuery = useQuery({
    queryKey: ["reviewActivity", activityReview?._id],
    queryFn: () => reviewService.getReviewActivity({ reviewId: activityReview!._id }),
    enabled: Boolean(activityReview?._id),
    staleTime: 0,
  });

  const factorKeysQuery = useQuery({
    queryKey: ["ratingFactorKeys", categorySlug],
    queryFn: () => reviewService.getRatingFactorKeys(categorySlug ?? ""),
    enabled: !!categorySlug,
    staleTime: 60 * 60 * 1000,
  });

  const createReviewMutation = useMutation({
    mutationFn: ({
      reviewData,
    }: {
      reviewData: CreateReviewRequest;
      imagesCount?: number;
    }) => reviewService.createReview({ storeId, reviewData }),
    onSuccess: (_data, { reviewData, imagesCount }) => {
      trackReview.created(storeId, reviewData.overallRating, {
        store_slug: storeSlug,
        factors_count: Object.values(reviewData.ratingFactors ?? {}).filter(
          (v) => typeof v === "number"
        ).length,
        images_count: imagesCount ?? 0,
        is_anonymous: !!reviewData.isAnonymous,
        surface: "store_page",
        store_status: storeStatus,
      });
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      setShowForm(false);
      setEditingReview(null);
      // No success toast here any more: the inline thank-you state below supersedes it, and a
      // toast on top of it would say the same thing twice. `updateReviewMutation` keeps ITS
      // toast — an edit is not a new contribution and must not show the thank-you state.
      //
      // The thank-you state itself is opened by `handleCreateReview` AFTER any images have
      // finished uploading: the photo bonus is paid by the upload, so reading the balance here
      // would show a figure 20 points short and then never correct it.
    },
    onError: () => {
      toast.error("حدث خطأ أثناء نشر التقييم");
    },
  });

  const updateReviewMutation = useMutation({
    mutationFn: ({
      reviewId,
      reviewData,
    }: {
      reviewId: string;
      reviewData: Partial<CreateReviewRequest>;
    }) => reviewService.updateReview({ reviewId, reviewData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      // The form is closed by handleCreateReview AFTER any image upload finishes. Clearing
      // `editingReview` here flipped the still-open form into an empty create form mid-save.
      toast.success("تم تحديث التقييم بنجاح!");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء تحديث التقييم");
    },
  });

  const likeMutation = useMutation({
    mutationFn: reviewService.toggleLike,
    onSuccess: (_data, reviewId) => {
      trackReview.liked(reviewId, storeId);
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
    },
    onError: () => {
      toast.error("حدث خطأ أثناء الإعجاب بالتقييم");
    },
  });

  const commentMutation = useMutation({
    mutationFn: ({ reviewId, text }: { reviewId: string; text: string }) =>
      reviewService.addComment({ reviewId, commentData: { text } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      toast.success("تم إضافة التعليق بنجاح!");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء إضافة التعليق");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: reviewService.deleteReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      toast.success("تم حذف التقييم بنجاح!");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء حذف التقييم");
    },
  });

  const reassignMutation = useMutation({
    mutationFn: ({ reviewId, newStoreId }: { reviewId: string; newStoreId: string }) =>
      reviewService.reassignReview({ reviewId, storeId: newStoreId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      setReassigningReview(null);
      setSelectedStore(null);
      setStoreSearchQuery("");
      setStoreSearchResults([]);
      toast.success("تم نقل التقييم بنجاح!");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء نقل التقييم");
    },
  });

  const unmatchMutation = useMutation({
    mutationFn: ({ reviewId }: { reviewId: string }) =>
      reviewService.unmatchReview({ reviewId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      setReassigningReview(null);
      setSelectedStore(null);
      setStoreSearchQuery("");
      setStoreSearchResults([]);
      toast.success("تم إلغاء ربط التقييم بالمتجر!");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء إلغاء ربط التقييم");
    },
  });

  const closeAssignUserDialog = () =>
    resetAssignUserDialog({
      setAssigningReview,
      setSelectedUser,
      setUserSearchQuery,
      setUserSearchResults,
    });

  const invalidateAfterUserLink = () => {
    queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
    queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
  };

  const assignUserMutation = useMutation({
    mutationFn: ({ reviewId, userId }: { reviewId: string; userId: string }) =>
      reviewService.assignReviewUser({ reviewId, userId }),
    onSuccess: (result: AssignReviewUserResult) => {
      invalidateAfterUserLink();
      closeAssignUserDialog();
      const awarded = result?.pointsAwarded ?? 0;
      toast.success(
        awarded > 0
          ? `تم ربط التقييم بالمستخدم وإضافة ${awarded} نقطة لحسابه`
          : "تم ربط التقييم بالمستخدم"
      );
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error("المستخدم ده عنده تقييم على المتجر ده أصلاً");
        return;
      }
      toast.error("حدث خطأ أثناء ربط التقييم بالمستخدم");
    },
  });

  const provenanceMutation = useMutation({
    mutationFn: ({
      reviewId,
      patch,
    }: {
      reviewId: string;
      patch: { sourceRemoved?: boolean; authorHidden?: ReviewAuthorHiddenLabel | false };
    }) => reviewService.setReviewProvenance({ reviewId, ...patch }),
    onSuccess: () => {
      invalidateAfterUserLink();
      toast.success("تم تحديث بيانات مصدر التقييم");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء تحديث بيانات المصدر");
    },
  });

  const unassignUserMutation = useMutation({
    mutationFn: ({ reviewId }: { reviewId: string }) =>
      reviewService.unassignReviewUser({ reviewId }),
    onSuccess: (result: AssignReviewUserResult) => {
      invalidateAfterUserLink();
      closeAssignUserDialog();
      const reversed = result?.pointsReversed ?? 0;
      toast.success(
        reversed > 0
          ? `تم فك ربط التقييم وخصم ${reversed} نقطة من حساب المستخدم`
          : "تم فك ربط التقييم"
      );
    },
    onError: () => {
      toast.error("حدث خطأ أثناء فك ربط التقييم");
    },
  });

  const adminEditRatingMutation = useMutation({
    mutationFn: ({ reviewId, rating }: { reviewId: string; rating: number }) =>
      reviewService.updateReview({ reviewId, reviewData: { overallRating: rating } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats", storeId] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      toast.success("تم تعديل التقييم!");
    },
    onError: () => {
      toast.error("حدث خطأ أثناء تعديل التقييم");
    },
  });

  const handleCreateReview = async (reviewData: CreateReviewRequest, images: File[] = []) => {
    if (editingReview) {
      await updateReviewMutation.mutateAsync({ reviewId: editingReview._id, reviewData });
      // Upload images to the existing review if any were added
      if (images.length > 0) {
        try {
          await reviewService.uploadReviewImages({ reviewId: editingReview._id, images });
          queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
        } catch {
          toast.error("فشل رفع الصور");
        }
      }
      // Saved: close the form and land on the updated card.
      setShowForm(false);
      setEditingReview(null);
      document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
    } else {
      // Read the balance BEFORE the award lands, so «كسبت {n} نقطة» is this review's
      // contribution rather than the user's lifetime total.
      await thankYou.captureBaseline();
      const result = await createReviewMutation.mutateAsync({
        reviewData,
        imagesCount: images.length,
      });
      // request() unwraps { ok, data: Review } → Review, so result is the Review object at runtime
      const newReviewId = (result as any)?._id;
      let uploadedImages = 0;
      if (images.length > 0 && newReviewId) {
        try {
          await reviewService.uploadReviewImages({ reviewId: newReviewId, images });
          uploadedImages = images.length;
          queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
        } catch {
          toast.error("فشل رفع الصور");
        }
      }
      await thankYou.open({
        rating: reviewData.overallRating,
        reviewId: newReviewId,
        composition: {
          text: reviewData.text ?? "",
          images: uploadedImages,
          factors: Object.values(reviewData.ratingFactors ?? {}).filter(
            (v) => typeof v === "number" && v > 0
          ).length,
        },
      });
    }
  };

  // ─── Thank-you upsells ──────────────────────────────────────────────────────
  //
  // All three write through the EXISTING review endpoints. The backend re-runs `awardForReview`
  // on both of them and the ledger's per-component unique index means only the newly earned
  // component pays, so no new endpoint exists for this and none is needed.
  const upsellReviewId = thankYou.reviewId;

  const handleUpsellError = () => toast.error("لم يتم الحفظ. حاول مرة أخرى.");

  const handleAddText = async (text: string) => {
    if (!upsellReviewId) return;
    try {
      await reviewService.updateReview({ reviewId: upsellReviewId, reviewData: { text } });
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      await thankYou.applyUpsell({ text });
    } catch {
      handleUpsellError();
    }
  };

  const handleAddPhoto = async (files: File[]) => {
    if (!upsellReviewId) return;
    try {
      await reviewService.uploadReviewImages({ reviewId: upsellReviewId, images: files });
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      await thankYou.applyUpsell({ images: files.length });
    } catch {
      toast.error("فشل رفع الصور");
    }
  };

  const handleAddFactors = async (factors: Record<string, number>) => {
    if (!upsellReviewId) return;
    try {
      await reviewService.updateReview({
        reviewId: upsellReviewId,
        reviewData: { ratingFactors: factors as CreateReviewRequest["ratingFactors"] },
      });
      queryClient.invalidateQueries({ queryKey: ["reviews", storeId] });
      await thankYou.applyUpsell({
        factors: Object.values(factors).filter((v) => v > 0).length,
      });
    } catch {
      handleUpsellError();
    }
  };

  useEffect(() => {
    if (!storeSearchQuery.trim()) {
      setStoreSearchResults([]);
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await requestWithAuth<StoreSearchHit[]>({
          path: `/fb-group-reviews/stores/search?q=${encodeURIComponent(storeSearchQuery)}`,
          method: "GET",
        });
        setStoreSearchResults(Array.isArray(result) ? result : []);
      } catch {
        setStoreSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [storeSearchQuery]);

  useEffect(() => {
    if (!userSearchQuery.trim()) {
      setUserSearchResults([]);
      return;
    }
    if (userSearchTimeout.current) clearTimeout(userSearchTimeout.current);
    userSearchTimeout.current = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        // Admin user list — searches name, email and phone on the server.
        const result = await requestWithAuth<{ users?: AdminUserHit[] }>({
          path: `/admin/users?search=${encodeURIComponent(userSearchQuery.trim())}&limit=8`,
          method: "GET",
        });
        setUserSearchResults(Array.isArray(result?.users) ? result.users : []);
      } catch {
        setUserSearchResults([]);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);
  }, [userSearchQuery]);

  const handleReassign = (review: Review) => {
    setReassigningReview(review);
    setSelectedStore(null);
    setStoreSearchQuery("");
    setStoreSearchResults([]);
  };

  const handleViewActivity = (review: Review) => {
    setActivityReview(review);
  };

  const handleAssignUser = (review: Review) => {
    setSelectedUser(null);
    setUserSearchQuery("");
    setUserSearchResults([]);
    setAssigningReview(review);
  };

  const handleSourceFilter = (src: string) => {
    setSourceFilter(src);
  };

  const handleRatingFilter = (rating: number) => {
    setRatingFilter((prev) => (prev === rating ? undefined : rating));
  };

  const handleEdit = (review: Review) => {
    thankYou.close();
    setEditingReview(review);
    setShowForm(true);
    // The form mounts at the TOP of the section, next to the add-review button. A native own
    // review sorts first so that was always in view; a linked imported review can sit pages
    // down, where the form opening looked like nothing happened. Same scroll the thank-you
    // upsells use when they reopen the form.
    document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (reviewId: string) => {
    if (confirm("هل أنت متأكد من حذف هذا التقييم؟")) {
      deleteMutation.mutate(reviewId);
    }
  };

  const reviews = reviewsData?.pages.flatMap((p) => p?.reviews ?? []) ?? [];
  // The pinned deep-link review is rendered once, above the list, never twice.
  const listedReviews = focusedReview
    ? reviews.filter((review) => review._id !== focusedReview._id)
    : reviews;
  // Returned on BOTH tabs, so the label stays correct after a switch. 0 → no tab strip at all,
  // and the section looks exactly as it did before this feature.
  const flaggedCount = reviewsData?.pages?.[0]?.flaggedCount ?? 0;
  // A linked imported review counts too: the server's one-review-per-store rule already does.
  const hasUserReviewed = user
    ? [...reviews, ...(focusedReview ? [focusedReview] : [])].some(
        (review) =>
          (!review.isExternal || Boolean(review.assignedUser)) && review.user?._id === user._id
      )
    : false;

  // Extracted so both tabs render IDENTICAL cards — a flagged review must keep every affordance
  // (like, comment, admin actions) it had before.
  const renderReviewCard = (
    review: Review,
    focus?: {
      defaultShowComments?: boolean;
      highlightCommentId?: string | null;
      isFocused?: boolean;
      highlighted?: boolean;
    }
  ) => (
    <ReviewCard
      key={review._id}
      {...focus}
      review={review}
      storeId={storeId}
      currentUserId={user?._id}
      isAdmin={user?.isAdmin}
      storeName={storeName}
      storeLogoUrl={storeLogoUrl}
      onLike={(id) => likeMutation.mutate(id)}
      onComment={async (id, text) => {
        await commentMutation.mutateAsync({ reviewId: id, text });
      }}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onReassign={user?.isAdmin ? handleReassign : undefined}
      onAssignUser={user?.isAdmin ? handleAssignUser : undefined}
      onViewActivity={user?.isAdmin ? handleViewActivity : undefined}
      onSetProvenance={
        user?.isAdmin
          ? (review, patch) => provenanceMutation.mutate({ reviewId: review._id, patch })
          : undefined
      }
      onFbComment={user?.isAdmin ? handleFbCommentFlow : undefined}
      onEditRating={
        user?.isAdmin
          ? (id, rating) => adminEditRatingMutation.mutate({ reviewId: id, rating })
          : undefined
      }
      isLoading={likeMutation.isPending || commentMutation.isPending}
    />
  );

  // Build source pill list from perSource stats
  const perSource: PerSourceStat[] = stats?.perSource ?? [];
  const availableSources = perSource.filter((s) => s.count > 0).map((s) => s.source);

  const getSourceCount = (src: string): number =>
    perSource.find((s) => s.source === src)?.count ?? 0;

  const totalReviews = stats?.totalReviews ?? 0;
  const withImagesCount = stats?.withImagesCount ?? 0;

  if (error) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "24px",
          background: "var(--ys-danger-soft, #fbe6e6)",
          borderRadius: "var(--ys-r-md)",
          border: "1px solid var(--ys-danger, #d24343)",
        }}
      >
        <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-danger, #d24343)" }}>
          حدث خطأ أثناء تحميل التقييمات
        </p>
      </div>
    );
  }

  return (
    <div id="reviews" className="scroll-mt-28 space-y-4">
      {/* FB comment confirm dialog */}
      <Dialog
        open={fbConfirmOpen}
        onOpenChange={(open) => { if (!open) handleFbConfirm(false); }}
      >
        <DialogContent dir="rtl" className="max-w-[92vw] gap-5 rounded-2xl border-slate-200 p-6 sm:max-w-md">
          <DialogHeader className="space-y-2 text-right sm:text-right">
            <DialogTitle className="text-2xl font-bold tracking-tight text-slate-950">
              هل تركت التعليق؟
            </DialogTitle>
            <DialogDescription className="text-base leading-7 text-slate-600">
              {fbPendingReview?.externalAuthor
                ? `هل علقت على منشور ${fbPendingReview.externalAuthor}؟`
                : "هل علقت على المنشور؟"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="grid grid-cols-2 gap-3 sm:justify-normal sm:space-x-0">
            <Button
              variant="outline"
              onClick={() => handleFbConfirm(false)}
              className="h-11 rounded-xl border-slate-200 text-base font-semibold"
            >
              لا، بعدين
            </Button>
            <Button
              onClick={() => handleFbConfirm(true)}
              disabled={fbMarking}
              className="h-11 gap-1.5 rounded-xl bg-slate-950 text-base font-semibold hover:bg-slate-800"
            >
              <CheckCircle2 className="w-4 h-4" />
              {fbMarking ? "جاري الحفظ..." : "نعم، تم"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draft store confirm dialog */}
      {showDraftConfirm && (
        <Dialog open={showDraftConfirm} onOpenChange={setShowDraftConfirm}>
          <DialogContent className="font-arabic">
            <DialogHeader>
              <DialogTitle>المتجر غير منشور بعد</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              هذا المتجر لم يُنشر للمستخدمين بعد. هل تريد إضافة تقييم على كل حال؟
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDraftConfirm(false)}
              >
                إلغاء
              </Button>
              <Button
                onClick={() => {
                  setShowDraftConfirm(false);
                  setShowForm(true);
                }}
              >
                إضافة تقييم على كل حال
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Header + add button */}
      <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
        <h3
          style={{
            fontFamily: "var(--ys-font)",
            fontWeight: 700,
            fontSize: 22,
            color: "var(--ys-ink)",
            margin: 0,
            display: "flex",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          كل التقييمات
          {totalReviews > 0 && (
            <span
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 14,
                fontWeight: 400,
                color: "var(--ys-ink-3)",
              }}
            >
              ({totalReviews})
            </span>
          )}
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flexWrap: "wrap",
            maxWidth: "100%",
          }}
        >
          <button
            onClick={handleShareReviews}
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 14,
              fontWeight: 600,
              background: "var(--ys-paper)",
              color: "var(--ys-ink-2)",
              border: "1px solid var(--ys-rule)",
              borderRadius: "var(--ys-r)",
              padding: "8px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Share2 style={{ width: 15, height: 15 }} />
            شارك التقييمات
          </button>
          {!hasUserReviewed && !showForm && (
            <button
              onClick={handleReviewCta}
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 14,
                fontWeight: 600,
                background: "var(--ys-primary)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--ys-r)",
                padding: "8px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Plus style={{ width: 15, height: 15 }} />
              أضف تقييمك
            </button>
          )}
        </div>
      </div>

      {/* Review Form */}
      {showForm && (
        <ReviewForm
          storeId={storeId}
          categorySlug={categorySlug ?? ""}
          onSubmit={(data, images) => handleCreateReview(data, images)}
          onCancel={() => { setShowForm(false); setEditingReview(null); }}
          existingReview={editingReview || undefined}
          isLoading={createReviewMutation.isPending || updateReviewMutation.isPending}
        />
      )}

      {/* Post-submit thank-you — replaces the old success toast. Tone follows the submitted
          rating, resolved inside ReviewThankYou. Cleared whenever the form reopens. */}
      {thankYouRating !== null && (
        <ReviewThankYou
          rating={thankYouRating}
          storeHref={`/store/${storeSlug || storeId}`}
          earnedPoints={thankYou.earnedPoints}
          nextTier={thankYou.nextTier}
          upsells={thankYou.upsells}
          factorKeys={factorKeysQuery.data ?? []}
          onAddText={handleAddText}
          onAddPhoto={handleAddPhoto}
          onAddFactors={handleAddFactors}
          onUpsell={(element) =>
            trackReview.thankYouUpsellClicked({
              surface: "store_page",
              element,
              points_delta:
                thankYou.upsells.find((u) => u.element === element)?.points ?? 0,
              rating: thankYouRating,
              ...storeTrackingProps,
            })
          }
          onSearchClick={() =>
            trackReview.thankYouSearchClicked({
              surface: "store_page",
              rating: thankYouRating,
              ...storeTrackingProps,
            })
          }
        />
      )}

      {/* Rating breakdown card */}
      {totalReviews > 0 && (
        <RatingBreakdown
          distribution={stats?.ratingDistribution ?? {}}
          averageRating={stats?.averageRating ?? 0}
          total={totalReviews}
          activeStars={ratingFilter}
          onSelectStars={handleRatingFilter}
          onClear={() => setRatingFilter(undefined)}
        />
      )}

      {/* Filter bar — only show if there are reviews */}
      {totalReviews > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Source pills */}
          {availableSources.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(["all", ...availableSources] as string[]).map((src) => {
                const active = src === "all" ? sourceFilter === "all" : sourceFilter === src;
                const label = src === "all"
                  ? `الكل (${totalReviews})`
                  : `${SOURCE_LABELS[src] ?? src} (${getSourceCount(src)})`;
                return (
                  <button
                    key={src}
                    onClick={() => handleSourceFilter(src === "all" ? "all" : src)}
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 13,
                      padding: "5px 14px",
                      borderRadius: "var(--ys-r-pill)",
                      border: `1px solid ${active ? "var(--ys-ink)" : "var(--ys-rule)"}`,
                      background: active ? "var(--ys-ink)" : "var(--ys-paper)",
                      color: active ? "#fff" : "var(--ys-ink-2)",
                      cursor: "pointer",
                      transition: "background 150ms, border-color 150ms, color 150ms",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Images toggle + sort */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            {/* «بالصور فقط» — hidden when no review on the store carries an image */}
            {withImagesCount > 0 ? (
              <button
                type="button"
                aria-pressed={imagesOnly}
                onClick={() => setImagesOnly((prev) => !prev)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  padding: "5px 14px",
                  borderRadius: "var(--ys-r-pill)",
                  border: `1px solid ${imagesOnly ? "var(--ys-ink)" : "var(--ys-rule)"}`,
                  background: imagesOnly ? "var(--ys-ink)" : "var(--ys-paper)",
                  color: imagesOnly ? "#fff" : "var(--ys-ink-2)",
                  cursor: "pointer",
                  transition: "background 150ms, border-color 150ms, color 150ms",
                }}
              >
                <Camera size={14} aria-hidden />
                بالصور فقط ({withImagesCount})
              </button>
            ) : (
              <span />
            )}

            {/* Sort */}
            <Select
              dir="rtl"
              value={sortBy}
              onValueChange={(value: string) => {
                setSortBy(value as typeof sortBy);
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">الأحدث</SelectItem>
                <SelectItem value="oldest">الأقدم</SelectItem>
                <SelectItem value="rating_high">التقييم الأعلى</SelectItem>
                <SelectItem value="rating_low">التقييم الأقل</SelectItem>
                <SelectItem value="most_liked">الأكثر إعجاباً</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Authenticity tabs — each tab server-fetches its own slice with its own pagination.
          The strip is absent entirely on a store with nothing flagged. It sits OUTSIDE the list
          block on purpose: a store whose reviews are ALL flagged has an empty trusted tab, and
          the reader still needs a way back across. RTL is inherited from the page. */}
      {flaggedCount > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {([
            { key: "trusted" as const, label: "التقييمات" },
            { key: "flagged" as const, label: `تقييمات مُستبعدة (${flaggedCount})` },
          ]).map(({ key, label }) => {
            const active = authTab === key;
            const warn = key === "flagged";
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  if (active) return;
                  setAuthTab(key);
                  // Fire only on switching TO the excluded tab, never on re-clicking it.
                  if (warn) trackStore.flaggedReviewsTabViewed({ ...storeTrackingProps });
                }}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  padding: "5px 14px",
                  borderRadius: "var(--ys-r-pill)",
                  border: `1px solid ${
                    active ? (warn ? "#f59e0b" : "var(--ys-ink)") : "var(--ys-rule)"
                  }`,
                  background: active
                    ? warn
                      ? "#fef3c7"
                      : "var(--ys-ink)"
                    : "var(--ys-paper)",
                  color: active ? (warn ? "#92400e" : "#fff") : "var(--ys-ink-2)",
                  cursor: "pointer",
                  transition: "background 150ms, border-color 150ms, color 150ms",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Explainer for the excluded tab — these reviews are shown, just not counted. */}
      {authTab === "flagged" && flaggedCount > 0 && (
        <div
          style={{
            fontFamily: "var(--ys-font)",
            fontSize: 13,
            color: "#92400e",
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "var(--ys-r-pill)",
            padding: "8px 16px",
          }}
        >
          هذه التقييمات مستوردة من صفحة المتجر ورُصدت ضمن حملة تقييمات — لا تُحتسب في تقييم المتجر.
        </div>
      )}

      {/* Notification deep-link target, pinned above the list (Phase 46, D-09). */}
      {focusedReview && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span
            style={{
              alignSelf: "flex-start",
              fontFamily: "var(--ys-font)",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ys-ink-2)",
              background: "var(--ys-bg-2)",
              borderRadius: "var(--ys-r-pill)",
              padding: "3px 10px",
            }}
          >
            {NOTIFICATION_CHROME.focusChip}
          </span>
          {renderReviewCard(focusedReview, {
            defaultShowComments: Boolean(focusTarget?.commentId),
            highlightCommentId: focusTarget?.commentId ?? null,
            isFocused: true,
            highlighted: focusHighlighted,
          })}
        </div>
      )}

      {/* Reviews list — one list, whichever tab is active. No client-side filtering. */}
      {reviews.length > 0 && (
        <div className="space-y-4">
          {listedReviews.map((review) => renderReviewCard(review))}

          {/* O2 (SEO): SSR'd crawlable link to the full paginated review archive.
              Rendered whenever there are more reviews than the SSR'd first page —
              this anchor is how the /reviews pages get discovered and crawled. */}
          {storeSlug && hasNextPage && (
            <div style={{ textAlign: "center" }}>
              <a
                href={`/store/${storeSlug}/reviews`}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  textDecoration: "underline",
                }}
              >
                عرض كل التقييمات
              </a>
            </div>
          )}

          {/* Load more — appends the next page below the current ones */}
          {hasNextPage && (
            <div style={{ textAlign: "center" }}>
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  background: "var(--ys-bg-2)",
                  border: "1px solid var(--ys-rule-2)",
                  borderRadius: "var(--ys-r-pill)",
                  padding: "9px 22px",
                  cursor: isFetchingNextPage ? "default" : "pointer",
                  opacity: isFetchingNextPage ? 0.6 : 1,
                }}
              >
                {isFetchingNextPage ? "جاري التحميل..." : "تحميل المزيد"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && reviews.length === 0 && totalReviews === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "48px 24px",
            background: "var(--ys-paper)",
            borderRadius: "var(--ys-r-md)",
            border: "1px solid var(--ys-rule)",
          }}
        >
          <Star style={{ width: 48, height: 48, color: "var(--ys-rule-2)", margin: "0 auto 12px" }} />
          <h3
            style={{
              fontFamily: "var(--ys-font)",
              fontWeight: 600,
              fontSize: 16,
              color: "var(--ys-ink)",
              margin: "0 0 8px",
            }}
          >
            لا توجد تقييمات حتى الآن
          </h3>
          <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-ink-3)", margin: "0 0 20px" }}>
            كن أول من يقيم {storeName}
          </p>
          <button
            onClick={handleReviewCta}
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 14,
              fontWeight: 600,
              background: "var(--ys-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "var(--ys-r)",
              padding: "9px 20px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Plus style={{ width: 15, height: 15 }} />
            أضف تقييمك
          </button>
        </div>
      )}

      {/* Empty filtered state */}
      {!isLoading && reviews.length === 0 && totalReviews > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "32px 24px",
            background: "var(--ys-paper)",
            borderRadius: "var(--ys-r-md)",
            border: "1px solid var(--ys-rule)",
          }}
        >
          <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-ink-3)" }}>
            لا توجد تقييمات تطابق الفلتر المحدد
          </p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "24px",
            background: "var(--ys-paper)",
            borderRadius: "var(--ys-r-md)",
            border: "1px solid var(--ys-rule)",
          }}
        >
          <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-ink-3)" }}>
            جاري تحميل التقييمات...
          </p>
        </div>
      )}
      {/* Reassign dialog */}
      <Dialog
        open={!!reassigningReview}
        onOpenChange={(open) => {
          if (!open) {
            setReassigningReview(null);
            setSelectedStore(null);
            setStoreSearchQuery("");
            setStoreSearchResults([]);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-arabic">نقل التقييم لمتجر آخر</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              placeholder="دوّر على متجر…"
              value={storeSearchQuery}
              onChange={(e) => {
                setStoreSearchQuery(e.target.value);
                setSelectedStore(null);
              }}
              className="font-arabic"
              autoFocus
            />
            {isSearching && (
              <p className="text-sm text-gray-500 font-arabic text-center">جاري البحث...</p>
            )}
            {storeSearchResults.length > 0 && !selectedStore && (
              <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                {storeSearchResults.map((store) => (
                  <button
                    key={store._id}
                    onClick={() => {
                      setSelectedStore(store);
                      setStoreSearchQuery(store.name);
                      setStoreSearchResults([]);
                    }}
                    className="w-full flex items-center gap-3 text-right px-3 py-2 hover:bg-gray-50 transition-colors"
                  >
                    {store.logoUrl ? (
                      <img
                        src={store.logoUrl}
                        alt=""
                        className="h-9 w-9 rounded-full object-cover border border-gray-200 shrink-0"
                      />
                    ) : (
                      <span className="h-9 w-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-sm font-semibold text-gray-500 shrink-0">
                        {store.name?.charAt(0)}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-arabic font-medium truncate">{store.name}</span>
                        {store.status && store.status !== "published" && (
                          <span className="text-[10px] font-arabic px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                            مسودة
                          </span>
                        )}
                      </span>
                      <span className="block text-xs text-gray-500 truncate text-right" dir="ltr">
                        {store.nameEn && store.nameEn !== store.name ? `${store.nameEn} · ` : ""}
                        <span className="font-mono">/{store.slug}</span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {selectedStore && (
              <p className="text-sm text-green-700 font-arabic bg-green-50 border border-green-200 rounded px-3 py-2">
                سيتم النقل إلى: <strong>{selectedStore.name}</strong>{" "}
                {selectedStore.slug && (
                  <span className="text-xs text-green-600 font-mono" dir="ltr">
                    (/{selectedStore.slug})
                  </span>
                )}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setReassigningReview(null);
                setSelectedStore(null);
                setStoreSearchQuery("");
                setStoreSearchResults([]);
              }}
              className="font-arabic"
            >
              إلغاء
            </Button>
            {reassigningReview?.externalSource === "facebook_group" && (
              <Button
                variant="outline"
                disabled={reassignMutation.isPending || unmatchMutation.isPending}
                onClick={() => {
                  if (reassigningReview) {
                    unmatchMutation.mutate({ reviewId: reassigningReview._id });
                  }
                }}
                className="font-arabic"
              >
                {unmatchMutation.isPending ? "جاري الإلغاء..." : "إلغاء الربط"}
              </Button>
            )}
            <Button
              disabled={!selectedStore || reassignMutation.isPending || unmatchMutation.isPending}
              onClick={() => {
                if (reassigningReview && selectedStore) {
                  reassignMutation.mutate({ reviewId: reassigningReview._id, newStoreId: selectedStore._id });
                }
              }}
              className="font-arabic"
            >
              {reassignMutation.isPending ? "جاري النقل..." : "نقل التقييم"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: link an imported review to a registered account */}
      <Dialog
        open={!!assigningReview}
        onOpenChange={(open) => {
          if (!open) closeAssignUserDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-arabic">ربط التقييم بمستخدم مسجّل</DialogTitle>
            <DialogDescription className="font-arabic text-right">
              التقييم هيظهر باسم المستخدم وعلى صفحته، وهيتم إضافة نقاط التقييم لحسابه. المصدر
              الأصلي بيتسجّل زي ما هو.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {assigningReview?.assignedUser && assigningReview.user && (
              <p className="text-sm text-blue-800 font-arabic bg-blue-50 border border-blue-200 rounded px-3 py-2">
                مرتبط حالياً بـ <strong>{assigningReview.user.full_name}</strong>
                {assigningReview.externalAuthor && (
                  <span className="text-xs text-blue-700"> (باسم {assigningReview.externalAuthor} في المصدر)</span>
                )}
              </p>
            )}
            <Input
              placeholder="دوّر بالاسم أو الإيميل أو رقم الموبايل…"
              value={userSearchQuery}
              onChange={(e) => {
                setUserSearchQuery(e.target.value);
                setSelectedUser(null);
              }}
              className="font-arabic"
              autoFocus
            />
            {isSearchingUsers && (
              <p className="text-sm text-gray-500 font-arabic text-center">جاري البحث...</p>
            )}
            {!isSearchingUsers &&
              userSearchQuery.trim() &&
              userSearchResults.length === 0 &&
              !selectedUser && (
                <p className="text-sm text-gray-500 font-arabic text-center">مفيش مستخدم بالاسم ده</p>
              )}
            {userSearchResults.length > 0 && !selectedUser && (
              <div className="border rounded-md divide-y max-h-56 overflow-y-auto">
                {userSearchResults.map((hit) => {
                  const name = hit.full_name?.trim() || "مستخدم";
                  return (
                    <button
                      key={hit._id}
                      onClick={() => {
                        setSelectedUser(hit);
                        setUserSearchQuery(name);
                        setUserSearchResults([]);
                      }}
                      className="w-full flex items-center gap-3 text-right px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      {hit.avatar_url ? (
                        <img
                          src={hit.avatar_url}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover border border-gray-200 shrink-0"
                        />
                      ) : (
                        <span className="h-9 w-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-sm font-semibold text-gray-500 shrink-0">
                          {name.charAt(0)}
                        </span>
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-arabic font-medium truncate">{name}</span>
                        <span className="block text-xs text-gray-500 truncate text-right" dir="ltr">
                          {[hit.email, hit.phoneNumber].filter(Boolean).join(" · ") || hit._id}
                        </span>
                      </span>
                      {typeof hit.points === "number" && (
                        <span className="text-xs text-gray-500 font-arabic shrink-0">{hit.points} نقطة</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {selectedUser && (
              <p className="text-sm text-green-700 font-arabic bg-green-50 border border-green-200 rounded px-3 py-2">
                سيتم الربط بـ: <strong>{selectedUser.full_name?.trim() || "مستخدم"}</strong>
                {(selectedUser.email || selectedUser.phoneNumber) && (
                  <span className="text-xs text-green-600" dir="ltr">
                    {" "}({selectedUser.email || selectedUser.phoneNumber})
                  </span>
                )}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeAssignUserDialog} className="font-arabic">
              إلغاء
            </Button>
            {assigningReview?.assignedUser && (
              <Button
                variant="outline"
                disabled={assignUserMutation.isPending || unassignUserMutation.isPending}
                onClick={() => {
                  if (assigningReview) {
                    unassignUserMutation.mutate({ reviewId: assigningReview._id });
                  }
                }}
                className="font-arabic"
              >
                {unassignUserMutation.isPending ? "جاري فك الربط..." : "فك الربط"}
              </Button>
            )}
            <Button
              disabled={!selectedUser || assignUserMutation.isPending || unassignUserMutation.isPending}
              onClick={() => {
                if (assigningReview && selectedUser) {
                  assignUserMutation.mutate({ reviewId: assigningReview._id, userId: selectedUser._id });
                }
              }}
              className="font-arabic"
            >
              {assignUserMutation.isPending ? "جاري الربط..." : "ربط التقييم"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: activity trail */}
      <Dialog
        open={!!activityReview}
        onOpenChange={(open) => {
          if (!open) setActivityReview(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-arabic">سجل التغييرات</DialogTitle>
            <DialogDescription className="font-arabic text-right">
              كل ما حدث للتقييم ده من لحظة إنشائه أو استيراده: التعديلات، النقل، الربط بمستخدم،
              والاستبعاد.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto py-1">
            {activityQuery.isLoading && (
              <p className="text-sm text-gray-500 font-arabic text-center py-6">جاري التحميل...</p>
            )}
            {activityQuery.isError && (
              <p className="text-sm text-red-600 font-arabic text-center py-6">
                حدث خطأ أثناء تحميل السجل
              </p>
            )}
            {activityQuery.data && activityQuery.data.activity.length === 0 && (
              <p className="text-sm text-gray-500 font-arabic text-center py-6">
                مفيش أي نشاط مسجّل على التقييم ده. السجل بدأ يتسجّل بعد إضافة الميزة، فالتقييمات
                الأقدم ممكن ميكون ليها تاريخ.
              </p>
            )}
            {activityQuery.data && activityQuery.data.activity.length > 0 && (
              <ol className="relative border-s border-gray-200 ms-3 space-y-4">
                {activityQuery.data.activity.map((row) => {
                  const awarded = Number(row.meta?.pointsAwarded ?? 0);
                  const reversed = Number(row.meta?.pointsReversed ?? 0);
                  return (
                    <li key={row.id} className="ms-4">
                      <span className="absolute -start-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-gray-300" />
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-sm font-arabic font-medium text-gray-900">
                          {ACTIVITY_ACTION_AR[row.action] ?? row.action}
                        </span>
                        <span className="text-xs text-gray-500 font-arabic">
                          {describeActivityActor(row.actor)}
                        </span>
                        <span className="text-xs text-gray-400 font-arabic" dir="ltr">
                          {new Date(row.createdAt).toLocaleString("ar-EG")}
                        </span>
                      </div>
                      {row.changes && row.changes.length > 0 && (
                        <ul className="mt-1 space-y-1">
                          {row.changes.map((c, i) => (
                            <li key={i} className="text-xs font-arabic text-gray-700">
                              <span className="font-medium">{ACTIVITY_FIELD_AR[c.field] ?? c.field}:</span>{" "}
                              <span className="line-through text-gray-400 break-words">
                                {formatActivityValue(c.before)}
                              </span>{" "}
                              <span className="text-gray-400">←</span>{" "}
                              <span className="break-words">{formatActivityValue(c.after)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {(awarded > 0 || reversed > 0) && (
                        <p className="mt-1 text-xs font-arabic text-gray-600">
                          {awarded > 0 && <span>+{awarded} نقطة</span>}
                          {awarded > 0 && reversed > 0 && <span> · </span>}
                          {reversed > 0 && <span>−{reversed} نقطة مسترجعة</span>}
                        </p>
                      )}
                      {typeof row.meta?.reason === "string" && row.meta.reason && (
                        <p className="mt-1 text-xs font-arabic text-gray-600">السبب: {row.meta.reason}</p>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityReview(null)} className="font-arabic">
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsSection;
